import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Gauge, LogOut, QrCode, Undo2, X } from 'lucide-react'
import { useAuth, roleHome } from '@/context/AuthContext'
import Avatar from '@/components/ui/Avatar'
import { categoriesOf, useStorefrontProducts } from '@/lib/useStorefrontCategories'
import { consoleFor } from '@/lib/roles'

/**
 * Full-screen navigation for the storefront below `sm`.
 *
 * The header keeps only search and cart at that width; everything else lives
 * here. The cart stays out in the bar on purpose - its badge is the only
 * standing reminder that someone has items waiting.
 *
 * Categories come from the catalog rather than a hardcoded list, the same way
 * the products page and the search palette derive them. The query is already
 * on the wire: SearchOverlay is mounted alongside this on every storefront
 * page and shares the key, so react-query serves this from cache.
 */
export default function MobileMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, logout, impersonating, endImpersonation } = useAuth()
  const products = useStorefrontProducts()
  const categories = categoriesOf(products.data)
  const location = useLocation()
  const navigate = useNavigate()

  const onLogout = async () => {
    onClose()
    if (impersonating) {
      // Back to being the admin, who lands on admin > Customers; not /login.
      await endImpersonation()
      return
    }
    await logout()
    navigate('/login')
  }
  const closeRef = useRef<HTMLButtonElement>(null)
  const reduce = useReducedMotion()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Every link closes the menu itself, but this also covers the browser back
  // button, which would otherwise leave the overlay sitting over a new page.
  // Compared against the previous path rather than keyed on it, so the effect
  // cannot fire on mount and close the menu as it opens.
  const lastPath = useRef(location.pathname)
  useEffect(() => {
    if (lastPath.current === location.pathname) return
    lastPath.current = location.pathname
    onClose()
  }, [location.pathname, onClose])

  // Rotating a phone to landscape crosses 640px on most devices, at which
  // point the header shows its full nav again and this overlay hides itself in
  // CSS. Closing it for real matters because the body scroll lock below would
  // otherwise stay on with nothing visible to explain why the page is stuck.
  useEffect(() => {
    if (!open) return
    const wideEnoughForTheBar = window.matchMedia('(min-width: 640px)')
    const closeIfWide = () => {
      if (wideEnoughForTheBar.matches) onClose()
    }
    closeIfWide()
    wideEnoughForTheBar.addEventListener('change', closeIfWide)
    return () => wideEnoughForTheBar.removeEventListener('change', closeIfWide)
  }, [open, onClose])

  // Without this the page behind scrolls under the user's finger while the
  // overlay is up. Nothing else in the app locks the body, so it is restored
  // to whatever it was rather than assumed to be "visible".
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  // autoFocus is unreliable inside a portal plus AnimatePresence tree, the
  // same reason SearchOverlay focuses its input imperatively.
  useEffect(() => {
    if (open) closeRef.current?.focus()
  }, [open])

  const consoleLabel = consoleFor(user).label

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          id="mobile-menu"
          className="fixed inset-0 z-50 flex flex-col bg-background sm:hidden"
          initial={reduce ? false : { opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 1 } : { opacity: 0, y: -8 }}
          transition={{ duration: reduce ? 0 : 0.18, ease: 'easeOut' }}
        >
          {/* Mirrors the header bar it replaces, down to its 56px mobile height. */}
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-foreground/10 px-4">
            <Link to="/" onClick={onClose} className="flex shrink-0 items-center">
              <img src="/logo_hd.png" alt="MPG Printing" className="h-6 w-auto object-contain" />
            </Link>
            <button
              type="button"
              ref={closeRef}
              onClick={onClose}
              aria-label="Close menu"
              className="flex size-10 items-center justify-center rounded-lg text-foreground/70 hover:bg-foreground/5 hover:text-foreground"
            >
              <X className="size-5" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-4 py-6">
            <Section label="Browse">
              <Row to="/" onClose={onClose}>
                Home
              </Row>
              <Row to="/products" onClose={onClose}>
                All products
              </Row>
            </Section>

            {categories.length > 0 && (
              <Section label="Categories">
                {categories.map((category) => (
                  <Row
                    key={category.name}
                    to={`/products?category=${encodeURIComponent(category.name)}`}
                    onClose={onClose}
                    meta={String(category.count)}
                  >
                    {category.name}
                  </Row>
                ))}
              </Section>
            )}

            <Section label="Account">
              {!user && (
                <>
                  <Row to="/login" onClose={onClose}>
                    Sign in
                  </Row>
                  <Row to="/register" onClose={onClose}>
                    Create free account
                  </Row>
                </>
              )}

              {user?.role === 'customer' && (
                <>
                  {/* Identity row - the mobile bar carries no avatar, so this
                      is the only place a signed-in customer sees who they are. */}
                  <div className="flex items-center gap-3 border-b border-foreground/10 py-3.5">
                    <Avatar user={user} className="size-10 shrink-0 bg-primary text-sm font-medium text-white" />
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-foreground">{user.name}</p>
                      <p className="truncate text-sm text-foreground/60">{user.email}</p>
                    </div>
                  </div>
                  <Row to="/account" onClose={onClose}>
                    My account
                  </Row>
                  <Row to="/account/orders" onClose={onClose}>
                    My orders
                  </Row>
                  <Row to="/account/wallet" onClose={onClose}>
                    Wallet and tiers
                  </Row>
                  <Row to="/account/settings" onClose={onClose}>
                    Settings
                  </Row>
                  {/* The only non-link row: the Row helper renders Links, and
                      signing out is an action rather than a destination. */}
                  <button
                    type="button"
                    onClick={onLogout}
                    className="flex w-full items-center gap-2 border-b border-foreground/10 py-3.5 text-left text-lg font-semibold text-foreground hover:text-primary"
                  >
                    {impersonating ? (
                      <>
                        <Undo2 className="size-4" />
                        Return to admin console
                      </>
                    ) : (
                      <>
                        <LogOut className="size-4" />
                        Sign out
                      </>
                    )}
                  </button>
                </>
              )}

              {user && user.role !== 'customer' && (
                <Row to={roleHome(user)} onClose={onClose}>
                  <span className="inline-flex items-center gap-2">
                    {user.role === 'admin' ? <Gauge className="size-4" /> : <QrCode className="size-4" />}
                    {consoleLabel}
                  </span>
                </Row>
              )}
            </Section>
          </nav>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}

/** Footer-style caption over a group of links. */
function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-7 last:mb-0">
      <p className="text-xs font-bold uppercase tracking-wide text-foreground/50">{label}</p>
      <div className="mt-1">{children}</div>
    </div>
  )
}

function Row({
  to,
  onClose,
  meta,
  children,
}: {
  to: string
  onClose: () => void
  meta?: string
  children: ReactNode
}) {
  return (
    <Link
      to={to}
      onClick={onClose}
      className="flex items-center justify-between border-b border-foreground/10 py-3.5 text-lg font-semibold text-foreground hover:text-primary"
    >
      {children}
      {meta && <span className="text-sm font-medium text-foreground/50">{meta}</span>}
    </Link>
  )
}
